import { useMemo } from 'react';
import { useQueryGp } from '@/query/gp/useQueryGp';
import useQuerySpaceflightNews from '@/query/news/useQuerySpaceflightNews';
import dayjs from 'dayjs';

export const useMonthlyData = (currentDate: dayjs.Dayjs) => {
  const currentMonthStart = dayjs.utc(currentDate).startOf('month');
  const lastMonthStart = dayjs
    .utc(currentDate)
    .subtract(1, 'month')
    .startOf('month');

  const { data: currentMonthGpData, isLoading: isCurrentMonthGpLoading } =
    useQueryGp(currentMonthStart);
  const { data: lastMonthGpData, isLoading: isLastMonthGpLoading } =
    useQueryGp(lastMonthStart);
  const { data: newsData, isLoading: isNewsLoading } =
    useQuerySpaceflightNews();

  const monthlyGpCount = useMemo(() => {
    if (!currentMonthGpData || !Array.isArray(currentMonthGpData.data)) {
      return null;
    }
    return currentMonthGpData.data.length;
  }, [currentMonthGpData]);

  const monthlyDebrisTrend = useMemo(() => {
    if (
      !currentMonthGpData ||
      !Array.isArray(currentMonthGpData.data) ||
      !lastMonthGpData ||
      !Array.isArray(lastMonthGpData.data)
    ) {
      return null;
    }
    const currentMonthDebris = currentMonthGpData.data.filter(
      item => item.objtype === 'DEBRIS'
    ).length;
    const lastMonthDebris = lastMonthGpData.data.filter(
      item => item.objtype === 'DEBRIS'
    ).length;

    return currentMonthDebris - lastMonthDebris;
  }, [currentMonthGpData, lastMonthGpData]);

  const monthlyPayloadsTrend = useMemo(() => {
    if (
      !currentMonthGpData ||
      !Array.isArray(currentMonthGpData.data) ||
      !lastMonthGpData ||
      !Array.isArray(lastMonthGpData.data)
    ) {
      return null;
    }

    const currentMonthPayloads = currentMonthGpData.data.filter(
      item => item.objtype === 'PAYLOAD'
    ).length;
    const lastMonthPayloads = lastMonthGpData.data.filter(
      item => item.objtype === 'PAYLOAD'
    ).length;

    return currentMonthPayloads - lastMonthPayloads;
  }, [currentMonthGpData, lastMonthGpData]);

  const monthlyKoreanSatellitesCount = useMemo(() => {
    if (!currentMonthGpData || !Array.isArray(currentMonthGpData.data)) {
      return null;
    }
    const koreanData = currentMonthGpData.data.filter(
      item => item.country === 'SKOR'
    );
    return koreanData.length;
  }, [currentMonthGpData]);

  return {
    // Raw data
    currentMonthGpData,
    lastMonthGpData,
    newsData,

    // Processed data
    monthlyGpCount,
    monthlyDebrisTrend,
    monthlyPayloadsTrend,
    monthlyKoreanSatellitesCount,

    // Loading states
    isCurrentMonthGpLoading,
    isLastMonthGpLoading,
    isNewsLoading,
  };
};
